import {
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useEsAdmin } from "../datos/useEsAdmin";
import { claveOrigen } from "../dominio/catalogos";
import { ordenSinAsignar } from "../dominio/equipo";
import { FILTRO_LEADS } from "../dominio/panel";
import type { Comercial, Negocio } from "../dominio/tipos";
import { formatoFecha } from "../negocios/formato";
import { useAbrirFicha } from "../negocios/useAbrirFicha";
import { PaginaAdmin } from "./PaginaAdmin";

/**
 * Sin asignar (S10): leads pendientes de llamar que no tienen dueño (los que Prospect ha
 * entregado y el reparto aún no ha colocado, los recuperados a los 7 días y los de un CSV
 * sin asignar). Jan y Robert los asignan a mano, uno o varios a la vez; el reparto
 * automático (cada 15 minutos) solo toma los de Prospect.
 */
export const SinAsignarPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const esAdmin = useEsAdmin();
  const abrirFicha = useAbrirFicha();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [destino, setDestino] = useState<string>("");
  const [asignando, setAsignando] = useState(false);

  const { data, isPending, refetch } = useGetList<Negocio>(
    "negocios",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "created_at", order: "ASC" },
      filter: { ...FILTRO_LEADS, "asignado_a@is": null },
    },
    { enabled: esAdmin },
  );
  const { data: usuarios } = useGetList<Comercial>(
    "sales",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "first_name", order: "ASC" },
    },
    { enabled: esAdmin },
  );
  const leads = useMemo(() => ordenSinAsignar(data ?? []), [data]);
  // 36: el consultor (Gabriel) no recibe leads: solo hace consultorías.
  const personas = (usuarios ?? []).filter(
    (u) => !u.disabled && u.rol_comercial !== "consultor",
  );

  const todos = leads.length > 0 && marcados.size === leads.length;
  const alternar = (id: Identifier) =>
    setMarcados((m) => {
      const n = new Set(m);
      const k = String(id);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const asignar = async () => {
    if (!destino || !marcados.size) return;
    setAsignando(true);
    try {
      const n = await dataProvider.asignar(
        [...marcados].map(Number),
        Number(destino),
      );
      notify("nevora.sin_asignar.asignados", {
        type: "success",
        messageArgs: { smart_count: n },
      });
      setMarcados(new Set());
      await refetch();
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setAsignando(false);
    }
  };

  const acciones = (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={destino} onValueChange={setDestino}>
        <SelectTrigger
          className="h-9 w-52"
          aria-label={translate("nevora.sin_asignar.a_quien")}
        >
          <SelectValue placeholder={translate("nevora.sin_asignar.a_quien")} />
        </SelectTrigger>
        <SelectContent>
          {personas.map((u) => (
            <SelectItem key={String(u.id)} value={String(u.id)}>
              {`${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()}
              {u.rol_comercial
                ? ` · ${translate(`nevora.equipo.roles.${u.rol_comercial}`)}`
                : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        disabled={!destino || !marcados.size || asignando}
        onClick={asignar}
      >
        {translate("nevora.sin_asignar.asignar", {
          smart_count: marcados.size,
        })}
      </Button>
    </div>
  );

  return (
    <PaginaAdmin
      titulo="nevora.sin_asignar.titulo"
      ayuda="nevora.sin_asignar.ayuda"
      acciones={acciones}
    >
      {isPending ? null : !leads.length ? (
        <p className="text-muted-foreground py-8 text-center">
          {translate("nevora.sin_asignar.vacio")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm" data-testid="sin-asignar">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="px-3 py-2 w-8">
                  <Checkbox
                    checked={todos}
                    aria-label={translate("nevora.sin_asignar.todos")}
                    onCheckedChange={(v) =>
                      setMarcados(
                        v ? new Set(leads.map((l) => String(l.id))) : new Set(),
                      )
                    }
                  />
                </th>
                <th className="px-2 py-2 text-left font-medium">
                  {translate("resources.negocios.fields.nombre")}
                </th>
                <th className="px-2 py-2 text-left font-medium">
                  {translate("resources.negocios.fields.sector")}
                </th>
                <th className="px-2 py-2 text-left font-medium">
                  {translate("resources.negocios.fields.origen")}
                </th>
                <th className="px-2 py-2 text-left font-medium">
                  {translate("nevora.sin_asignar.desde")}
                </th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={String(l.id)} className="border-b last:border-b-0">
                  <td className="px-3 py-2">
                    <Checkbox
                      checked={marcados.has(String(l.id))}
                      aria-label={l.nombre}
                      onCheckedChange={() => alternar(l.id)}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <button
                      type="button"
                      className="font-medium underline-offset-2 hover:underline text-left"
                      onClick={() => abrirFicha(l.negocio_id)}
                    >
                      {l.nombre}
                    </button>
                    {l.prospect_prioridad ? (
                      <Badge variant="outline" className="ml-2">
                        {translate("nevora.para_llamar.prioridad", {
                          n: l.prospect_prioridad,
                        })}
                      </Badge>
                    ) : null}
                    {l.prospect_frio_at ? (
                      <Badge variant="outline" className="ml-2">
                        {translate("nevora.para_llamar.frio")}
                      </Badge>
                    ) : null}
                    {l.recuperado_at ? (
                      <Badge variant="secondary" className="ml-2">
                        {translate("nevora.sin_asignar.recuperado")}
                      </Badge>
                    ) : null}
                  </td>
                  <td className="px-2 py-2">{l.sector ?? ""}</td>
                  <td className="px-2 py-2">
                    {l.origen ? translate(claveOrigen(l.origen)) : ""}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {formatoFecha(
                      l.recuperado_at ??
                        l.prospect_entregado_at ??
                        l.created_at,
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PaginaAdmin>
  );
};

SinAsignarPage.path = "/sin-asignar";
